import { useId } from 'react'

/**
 * LAMS identity — Concept C, "Land Layers".
 * Source: LAMS_Branding_Definitions.md sections 4, 6, 7 and the approved brand sheet.
 *
 * Four stacked cartographic surfaces read top to bottom as Land, Water, Stewardship and Data,
 * with a river flowing through them and GIS nodes on the corners. Drawn as vector so it holds
 * at favicon size, and it renders in a single colour when `mono` is set (section 7 requires
 * single-colour use).
 *
 * Cartographic surfaces, not database disks.
 */
export function LamsIcon({
  size = 32,
  mono = false,
  title,
}: {
  size?: number
  /** Render in one colour — for monochrome print, or on a coloured ground. */
  mono?: boolean
  /** Accessible name. Omit when the icon sits beside the wordmark. */
  title?: string
}) {
  const id = useId()
  const uid = (n: string) => `${id}-${n}`

  /* Isometric plate: a diamond, drawn from its centre. */
  const plate = (cy: number) => `M50 ${cy - 21} L92 ${cy} L50 ${cy + 21} L8 ${cy} Z`

  const LAYERS = [
    { cy: 26, fill: mono ? 'currentColor' : `url(#${uid('land')})`, op: mono ? 1 : 1 },
    { cy: 47, fill: mono ? 'currentColor' : `url(#${uid('water')})`, op: mono ? 0.72 : 1 },
    { cy: 68, fill: mono ? 'currentColor' : `url(#${uid('stew')})`, op: mono ? 0.5 : 1 },
    { cy: 89, fill: mono ? 'currentColor' : `url(#${uid('data')})`, op: mono ? 0.3 : 1 },
  ]

  return (
    <svg
      viewBox="0 0 100 116"
      width={size}
      height={(size * 116) / 100}
      fill="none"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title ? <title>{title}</title> : null}
      {!mono ? (
        <defs>
          <linearGradient id={uid('land')} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#6FA65A" />
            <stop offset="100%" stopColor="#4A7539" />
          </linearGradient>
          <linearGradient id={uid('water')} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#4B90C5" />
            <stop offset="100%" stopColor="#266799" />
          </linearGradient>
          <linearGradient id={uid('stew')} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#F7C965" />
            <stop offset="100%" stopColor="#E0A41F" />
          </linearGradient>
          <linearGradient id={uid('data')} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#DCEAF5" />
            <stop offset="100%" stopColor="#B9D3E8" />
          </linearGradient>
          <clipPath id={uid('clipLand')}>
            <path d={plate(26)} />
          </clipPath>
          <clipPath id={uid('clipData')}>
            <path d={plate(89)} />
          </clipPath>
        </defs>
      ) : null}

      {/* Plates, bottom first so upper layers sit in front. */}
      {[...LAYERS].reverse().map((l) => (
        <path key={l.cy} d={plate(l.cy)} fill={l.fill} opacity={l.op} />
      ))}

      {!mono ? (
        <>
          {/* Contour lines on the land plate. */}
          <g clipPath={`url(#${uid('clipLand')})`} stroke="#FFFFFF" strokeOpacity="0.28" fill="none" strokeWidth="1">
            <path d="M18 24 C 30 16, 42 30, 56 22" />
            <path d="M22 31 C 34 23, 46 37, 60 29" />
            <path d="M62 14 C 70 18, 78 16, 86 21" />
          </g>
          {/* Grid on the data plate. */}
          <g clipPath={`url(#${uid('clipData')})`} stroke="#FFFFFF" strokeOpacity="0.55" fill="none" strokeWidth="0.9">
            <path d="M29 79 L71 100 M50 68 L92 89 M8 89 L50 110" />
            <path d="M71 79 L29 100 M50 68 L8 89 M92 89 L50 110" />
          </g>
        </>
      ) : null}

      {/*
       * The river. It threads the stack and subtly forms an S for Suwannee
       * (branding definitions, section 3.3).
       */}
      <path
        d="M44 12 C 58 22, 34 30, 48 40 C 62 50, 38 58, 52 68 C 64 77, 44 84, 54 94"
        stroke={mono ? 'currentColor' : '#FFFFFF'}
        strokeOpacity={mono ? 0.95 : 0.92}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <path
        d="M44 12 C 58 22, 34 30, 48 40 C 62 50, 38 58, 52 68 C 64 77, 44 84, 54 94"
        stroke={mono ? 'currentColor' : '#2E7EBB'}
        strokeOpacity={mono ? 0.45 : 0.9}
        strokeWidth="3"
        strokeLinecap="round"
      />

      {/* GIS vertex nodes. */}
      {[
        [50, 5],
        [8, 26],
        [8, 68],
        [92, 47],
        [50, 110],
      ].map(([cx, cy]) => (
        <circle
          key={`${cx}-${cy}`}
          cx={cx}
          cy={cy}
          r="5"
          fill={mono ? 'currentColor' : '#0F3B66'}
          stroke={mono ? 'none' : '#FFFFFF'}
          strokeWidth="2"
        />
      ))}
    </svg>
  )
}

/**
 * Brand lockup. Horizontal for the masthead, stacked for the cover.
 * Wordmark uppercase; the full name in a lighter weight (branding definitions, section 6).
 */
export function LamsLockup({
  variant = 'horizontal',
  iconSize,
  showFullName = true,
  tagline = false,
}: {
  variant?: 'horizontal' | 'stacked'
  iconSize?: number
  showFullName?: boolean
  /** Show the approved Concept C tagline beneath the lockup. */
  tagline?: boolean
}) {
  const size = iconSize ?? (variant === 'stacked' ? 96 : 30)
  return (
    <span className={`lockup lockup--${variant}`}>
      <LamsIcon size={size} />
      <span className="lockup__type">
        <span className="lockup__wordmark">LAMS</span>
        {showFullName ? (
          <span className="lockup__fullname">Land Acquisition &amp; Management System</span>
        ) : null}
      </span>
      {tagline ? (
        <span className="lockup__tagline">Geospatial intelligence for land stewardship</span>
      ) : null}
    </span>
  )
}
